import { createLoggerWithPrefix } from '@shared/utils/logger'
import {
  pagedTenantResponseSchema,
  pagedTenantDataSchema,
  tenantSingleResponseSchema,
  tenantDtoSchema,
  tenantListDataSchema,
} from '@application/dto/tenant.dto'
import type { Tenant } from '@domain/entities'

const logger = createLoggerWithPrefix('tenantMapper')

const mapDto = (dto: {
  id: string
  name: string
  slug: string
  status: string
  description?: string | null
  plan?: string | null
  maxUsers?: number | null
  createdAt: string
  updatedAt?: string | null
}): Tenant => ({
  id: dto.id,
  name: dto.name,
  slug: dto.slug,
  status: dto.status as Tenant['status'],
  description: dto.description ?? undefined,
  plan: dto.plan ?? undefined,
  maxUsers: dto.maxUsers ?? undefined,
  createdAt: dto.createdAt,
  updatedAt: dto.updatedAt ?? undefined,
})

export interface PagedTenants {
  items: Tenant[]
  totalCount: number
  page: number
  pageSize: number
}

export const mapTenantsResponse = (response: unknown): PagedTenants | null => {
  if (!response || typeof response !== 'object') {
    logger.error('Tenants response is null or not an object')
    return null
  }

  // Try wrapped envelope: { success, data: { items, totalCount, page, pageSize } }
  const wrapperResult = pagedTenantResponseSchema.safeParse(response)
  if (wrapperResult.success) {
    const { data } = wrapperResult.data
    return {
      items: data.items.map(mapDto),
      totalCount: data.totalCount,
      page: data.page,
      pageSize: data.pageSize,
    }
  }

  // Fallback: try unwrapped paged
  const responseObj = response as Record<string, unknown>
  const rawData = responseObj.data ?? response
  const dataResult = pagedTenantDataSchema.safeParse(rawData)
  if (dataResult.success) {
    return {
      items: dataResult.data.items.map(mapDto),
      totalCount: dataResult.data.totalCount,
      page: dataResult.data.page,
      pageSize: dataResult.data.pageSize,
    }
  }

  // Fallback: bare array
  const arrayResult = tenantListDataSchema.safeParse(rawData)
  if (arrayResult.success) {
    return {
      items: arrayResult.data.map(mapDto),
      totalCount: arrayResult.data.length,
      page: 1,
      pageSize: arrayResult.data.length,
    }
  }

  logger.error('Tenants validation failed:', dataResult.error.format())
  return null
}

export const mapTenantResponse = (response: unknown): Tenant | null => {
  if (!response || typeof response !== 'object') {
    logger.error('Tenant response is null or not an object')
    return null
  }

  const wrapperResult = tenantSingleResponseSchema.safeParse(response)
  if (wrapperResult.success) {
    return mapDto(wrapperResult.data.data)
  }

  const responseObj = response as Record<string, unknown>
  const rawData = responseObj.data ?? response
  const dataResult = tenantDtoSchema.safeParse(rawData)
  if (dataResult.success) {
    return mapDto(dataResult.data)
  }

  logger.error('Tenant validation failed:', dataResult.error.format())
  return null
}
